import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useLocalState from '@phntms/use-local-state'
import { Button, Spin, Table, Space, message } from 'antd'
import { FilterFilled, DownloadOutlined, SettingOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { utils, writeFileXLSX } from 'xlsx'
import { userSelector } from '../../../store/session'
import { archiveApplicationItem, getApplicationItemsByProject } from '../../../services/application-items'
import { createApplication } from '../../../services/application'

import { setApplicationItem } from '../../../store/application'
import getSearchFilter from '../../table/serachFilter'
import getListFilter from '../../table/listFilter'
import ApplicationSelectModal from '../ApplicationSelectModal'
import { getUsers } from '../../../services/user'

import ColumnsSettings from '../../table/columnsSettings'
import styles from './style.module.css'
import useCategory from '../../hooks/useCategory'
import usePagination from '../../hooks/usePagination'
import { APP_STATUS_PROGRESS } from '../../reports/constants'
import { getOffBudget } from '../app-view-utils'
import { formatDecimalAmount, formatMoney } from '../../../utils/number-utils'
import ProtectedAccess from '../../RoleBasedAccess/ProtectedAccess'

const columnsDefaultSettings = [
  {
    key: 'estimate_name',
    name: 'Позиция Смета',
    display: true,
    index: 0,
  },
  {
    key: 'estimate_category',
    name: 'Категория',
    display: true,
    index: 1,
  },
  {
    key: 'off_budget',
    name: 'Вне сметы',
    display: true,
    index: 2,
  },
  {
    key: 'estimate_price',
    name: 'Цена смета',
    display: true,
    index: 3,
  },
  {
    key: 'estimate_amount',
    name: 'Кол-во смета',
    display: true,
    index: 4,
  },
  {
    key: 'limits',
    name: 'Фактически заказано',
    display: true,
    index: 5,
  },
  {
    key: 'delivered',
    name: 'Фактически доставлено',
    display: true,
    index: 6,
  },
  {
    key: 'name',
    name: 'Наименование',
    display: true,
    index: 7,
  },
  {
    key: 'description',
    name: 'Примечание',
    display: true,
    index: 8,
  },
  {
    key: 'amount',
    name: 'Кол-во',
    display: true,
    index: 9,
  },
  {
    key: 'unit',
    name: 'Ед.из.',
    display: true,
    index: 10,
  },
  {
    key: 'user',
    name: 'Прораб',
    display: true,
    index: 11,
  },
  {
    key: 'updatedAt',
    name: 'Дата',
    display: true,
    index: 12,
  },
  {
    key: 'project',
    name: 'Проект',
    display: true,
    index: 13,
  },
]

const settingsKey = 'ApplicationItemsTable'

const getFilterIcon = (condition) => () =>
  (
    <FilterFilled
      style={{
        color: condition ? '#1890ff' : undefined,
      }}
    />
  )

const baseCellStyle = {
  height: '100%',
  padding: 8,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}

const numberCellStyle = {
  padding: 8,
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const getCellOverStyle = (isOver) => ({
  backgroundColor: isOver ? '#eca8b2' : 'white',
})

const ApplicationItemsTable = ({ isActive }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector(userSelector)
  const [categories] = useCategory()
  const [isLoading, setIsLoading] = useState(false)
  const [showAddMobile, setShowAddMobile] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [applicationItems, setApplicationItems] = useState([])
  const [pageTotal, setPageTotal] = useState(0)
  const [tablePagination, page, pageSize, setPage] = usePagination(pageTotal)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [filterInput, setFilterInput] = useState([])
  const [filter, setFilter] = useState({
    estimate_name: '',
    estimate_category: [],
    name: '',
    description: '',
    users: [],
    projects: [],
  })
  const [selectedCategories, setSelectedCategories] = useState([])
  const [foremans, setForemans] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [appEstimateAmounts, setAppEstimateAmounts] = useState({})
  const [implEstimateAmounts, setImplEstimateAmounts] = useState({})
  const [isTableSettingsVisible, setTableSettingsVisible] = useState(false)
  const [storedData, setStoredData] = useLocalState(settingsKey, columnsDefaultSettings)
  const [filteredColumns, setFilteredColumns] = useState([])

  const users = useMemo(() => foremans.map((item) => `${item.first_name} ${item.second_name}`), [foremans])

  const isForeman = useMemo(() => user?.role?.type === 'foreman', [user])

  useEffect(() => {
    const params = {
      fields: ['id', 'first_name', 'second_name'],
      filters: {
        role: {
          type: 'foreman',
        },
      },
    }
    getUsers(params)
      .then((response) => {
        setForemans(response.data)
      })
      .catch(() => {
        // TODO error handling
      })
  }, [])

  const onUpdateHandler = (record) => {
    dispatch(setApplicationItem({ applicationItem: record }))
    navigate('/application/new')
  }

  const onArchiveHandler = (applicationItem) => {
    setIsLoading(true)
    archiveApplicationItem(applicationItem.id)
      .then((response) => {
        message.success('Заявка успешно удалена')
      })
      .catch((err) => {
        const errText = err?.response?.data?.error?.message
        if (errText) {
          message.error(`Ошибка удаления заявки: ${errText}`)
        } else {
          message.error('Ошибка удаления заявки')
        }
      })
      .finally(() => {
        setIsLoading(false)
        getApplications()
      })
  }

  const getFilterOnChangeHandler = (index) => (value) => {
    const newArray = [...filterInput]
    newArray[index] = value
    setFilterInput(newArray)
  }

  const getFilterOnSearchHandler = (field, index) => () => {
    setPage(1)
    setFilter({
      ...filter,
      [field]: filterInput[index],
    })
  }

  const getFilterOnClearHandler = (field, index) => () => {
    const newArray = [...filterInput]
    newArray[index] = ''
    setFilterInput(newArray)
    setPage(1)
    setFilter({
      ...filter,
      [field]: '',
    })
  }

  const columns = [
    {
      title: 'Номер',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Позиция Смета',
      dataIndex: 'estimate_field',
      key: 'estimate_name',
      render: (estimate_field) => estimate_field?.name,
      filterDropdown: getSearchFilter(
        filterInput[0],
        getFilterOnChangeHandler(0),
        getFilterOnSearchHandler('estimate_name', 0),
        getFilterOnClearHandler('estimate_name', 0)
      ),
      filterIcon: getFilterIcon(filter?.estimate_name?.length > 0),
    },
    {
      title: 'Категория',
      dataIndex: 'estimate_field',
      key: 'estimate_category',
      render: (estimate_field, record) => estimate_field?.category || record?.category,
      filterDropdown: getListFilter(
        categories,
        selectedCategories,
        setSelectedCategories,
        () => {
          setPage(1)
          setFilter({
            ...filter,
            estimate_category: selectedCategories,
          })
        },
        () => {
          setSelectedCategories([])
          setPage(1)
          setFilter({
            ...filter,
            estimate_category: [],
          })
        }
      ),
      filterIcon: getFilterIcon(filter?.estimate_category?.length > 0),
    },
    {
      title: 'Вне сметы',
      dataIndex: 'off_budget',
      key: 'off_budget',
      width: 150,
      render: (off_budget) => getOffBudget(off_budget),
    },
    {
      title: 'Цена смета без НДС',
      dataIndex: 'estimate_field',
      key: 'estimate_price',
      width: 150,
      render: (estimate_field) =>
        estimate_field ? `${formatMoney(estimate_field?.price)} / ${estimate_field?.unit}` : '',
    },
    {
      title: 'Кол-во смета',
      dataIndex: 'estimate_field',
      key: 'estimate_amount',
      width: 150,
      render: (estimate_field) =>
        estimate_field ? `${formatDecimalAmount(estimate_field?.amount)} ${estimate_field?.unit}` : '',
    },
    {
      title: () => <div style={{ padding: 8 }}>Фактически заказано</div>,
      dataIndex: 'estimate_field',
      key: 'limits',
      className: 'no-padding-cell-report',
      width: 120,
      render: (estimate_field, record) => {
        if (appEstimateAmounts && appEstimateAmounts.length > 0) {
          const estimateLimits = appEstimateAmounts.filter((item) => item?.id === estimate_field?.id)
          if (estimateLimits && estimateLimits.length > 0) {
            const estimateAmounts = estimateLimits.reduce((acc, item) => acc + item.application_sum, 0)
            const isOver = record?.estimate_field
              ? record?.estimate_field?.amount < estimateAmounts + record?.amount
              : false
            return (
              <div style={{ ...baseCellStyle, ...getCellOverStyle(isOver) }}>
                {estimateLimits.map((limit) => (
                  <div>{`${formatDecimalAmount(limit.application_sum)} / ${limit.unit}`}</div>
                ))}
              </div>
            )
          }
        }
        return ''
      },
    },
    {
      title: () => <div style={{ padding: 8 }}>Фактически доставлено</div>,
      dataIndex: 'estimate_field',
      key: 'delivered',
      className: 'no-padding-cell-report',
      width: 120,
      render: (estimate_field, record) => {
        if (implEstimateAmounts && implEstimateAmounts.length > 0) {
          const estimateAmounts = implEstimateAmounts.filter((item) => item?.id === estimate_field?.id)
          if (estimateAmounts && estimateAmounts.length > 0) {
            const totalAmounts = estimateAmounts.reduce((acc, item) => acc + item.total_amount, 0)
            const isOver = record?.estimate_field
              ? record?.estimate_field?.amount < totalAmounts + record?.amount
              : false
            return (
              <div style={{ ...baseCellStyle, ...getCellOverStyle(isOver) }}>
                {estimateAmounts
                  .filter((item) => item.unit != null && item.total_amount > 0)
                  .map((item) => (
                    <div>{`${formatDecimalAmount(item.total_amount)}${item.unit ? ` / ${item.unit}` : ''}`}</div>
                  ))}
              </div>
            )
          }
        }
        return ''
      },
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: getSearchFilter(
        filterInput[1],
        getFilterOnChangeHandler(1),
        getFilterOnSearchHandler('name', 1),
        getFilterOnClearHandler('name', 1)
      ),
      filterIcon: getFilterIcon(filter?.name?.length > 0),
    },
    {
      title: 'Примечание',
      dataIndex: 'description',
      key: 'description',
      filterDropdown: getSearchFilter(
        filterInput[2],
        getFilterOnChangeHandler(2),
        getFilterOnSearchHandler('description', 2),
        getFilterOnClearHandler('description', 2)
      ),
      filterIcon: getFilterIcon(filter?.description?.length > 0),
    },
    {
      title: () => <div style={{ padding: 8 }}>Кол-во</div>,
      dataIndex: 'amount',
      key: 'amount',
      className: 'no-padding-cell-report',
      width: 100,
      render: (amount, record) => {
        const isOver = record?.estimate_field ? record?.estimate_field?.amount < amount : false
        return <div style={{ ...numberCellStyle, ...getCellOverStyle(isOver) }}>{formatDecimalAmount(amount)}</div>
      },
    },
    {
      title: 'Ед.из.',
      dataIndex: 'unit',
      width: 60,
      key: 'unit',
    },
    {
      title: 'Прораб',
      dataIndex: 'user',
      key: 'user',
      width: 220,
      render: (user) => `${user?.first_name || ''} ${user?.second_name || ''}`,
      filterDropdown: getListFilter(
        users,
        selectedUsers,
        setSelectedUsers,
        () => {
          setPage(1)
          setFilter({
            ...filter,
            users: foremans
              .filter((item) => selectedUsers.includes(`${item.first_name} ${item.second_name}`))
              .map((item) => item.id),
          })
        },
        () => {
          setSelectedUsers([])
          setPage(1)
          setFilter({
            ...filter,
            users: [],
          })
        }
      ),
      filterIcon: getFilterIcon(filter?.users?.length > 0),
    },
    {
      title: 'Дата',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 100,
      render: (updatedAt) => format(new Date(updatedAt), 'dd-MM-yyyy'),
    },
    {
      title: 'Проект',
      dataIndex: 'project',
      key: 'project',
      width: 300,
      render: (project) => project?.name,
      filterDropdown: getListFilter(
        user?.projects?.map((item) => item.name),
        selectedProjects,
        setSelectedProjects,
        () => {
          setPage(1)
          setFilter({
            ...filter,
            projects: user?.projects?.filter((item) => selectedProjects.includes(item.name)).map((item) => item.id),
          })
        },
        () => {
          setSelectedProjects([])
          setPage(1)
          setFilter({
            ...filter,
            projects: [],
          })
        }
      ),
      filterIcon: getFilterIcon(filter?.projects?.length > 0),
    },
    {
      title: 'Действия',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 180,
      render: (text, record) => (
        <Space size="middle" direction="vertical">
          <ProtectedAccess allowedRoles={['foreman']}>
            <Button disabled={!isForeman} onClick={() => onUpdateHandler(record)} type="dashed">
              Редактировать
            </Button>
          </ProtectedAccess>
          <ProtectedAccess allowedRoles={['supplier', 'project_manager']}>
            <Button onClick={() => onUpdateHandler(record)} type="dashed">
              Просмотреть
            </Button>
          </ProtectedAccess>
          <ProtectedAccess allowedRoles={['foreman', 'project_manager']}>
            <Button onClick={() => onArchiveHandler(record)} danger>
              Удалить
            </Button>
          </ProtectedAccess>
          <ProtectedAccess notAllowedRoles={['foreman']}>
            <Button
              onClick={() => {
                setSelectedItem(record)
                setShowAddMobile(true)
              }}
              type="dashed"
            >
              Добавить в заявку
            </Button>
          </ProtectedAccess>
        </Space>
      ),
    },
  ]

  useEffect(() => {
    const filtered = storedData
      .filter((item) => item?.display)
      .map((item) => {
        return columns.find((column) => column.key === item.key)
      })
    filtered.unshift(columns[0])
    filtered.push(columns[columns.length - 1])
    setFilteredColumns(filtered)
  }, [
    user,
    users,
    appEstimateAmounts,
    implEstimateAmounts,
    selectedUsers,
    categories,
    selectedCategories,
    selectedProjects,
    filterInput,
    filter,
    storedData,
  ])

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys),
    preserveSelectedRowKeys: true,
  }

  const getApplications = () => {
    setIsLoading(true)
    const ids = user?.projects.map((item) => item.id)

    const pagination = { page, pageSize }
    getApplicationItemsByProject(ids, pagination, filter)
      .then(({ data }) => {
        setApplicationItems(data?.data?.attributes?.results)
        setAppEstimateAmounts(data?.data?.attributes?.app_estimate_amounts)
        setImplEstimateAmounts(data?.data?.attributes?.impl_estimate_amounts)
        setPageTotal(data?.data?.attributes?.pagination?.total)
      })
      .catch(() => {
        message.error('Ошибка получения данных о заявках')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (user && user?.projects && isActive) {
      getApplications()
    }
  }, [user, page, pageSize, filter, isActive])

  const createApplicationHandler = () => {
    if (selectedKeys.length === 0) {
      message.error('Не выбрано ни одной заявки')
      return
    }

    const projectIds = new Set()
    const projectId = selectedKeys.reduce((acc, item) => {
      const project = applicationItems.find((application) => application.id === item)
      projectIds.add(project?.project?.id)
      return project?.project?.id
    }, null)

    if (projectIds.size > 1) {
      message.error('Выбраны заявки из разных проектов')
      return
    }

    setIsLoading(true)

    const data = {
      data: {
        project: projectId,
        user: user.id,
        current_state: APP_STATUS_PROGRESS,
      },
      items: selectedKeys,
    }

    createApplication(data)
      .then(() => {
        message.success('Заявка успешно сформирована')
        setSelectedKeys([])
        getApplications()
      })
      .catch((error) => message.error('Ошибка при формировании заявки'))
      .finally(() => {
        setIsLoading(false)
      })
  }

  const exportToXLS = () => {
    const rows = applicationItems.map((item) => {
      return {
        id: item.id,
        estimate_name: item?.estimate_field?.name,
        estimate_category: item?.estimate_field?.category,
        estimate_price: item?.estimate_field ? `${item?.estimate_field?.price} / ${item?.estimate_field?.unit}` : '',
        name: item?.name,
        description: item?.description,
        amount: item?.amount,
        unit: item?.unit,
        user: `${item?.user?.first_name || ''} ${item?.user?.second_name || ''}`,
        updatedAt: format(new Date(item?.updatedAt), 'dd-MM-yyyy'),
        project: item?.project?.name,
      }
    })
    const worksheet = utils.json_to_sheet(rows)
    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, 'Заявки')
    utils.sheet_add_aoa(
      worksheet,
      [
        [
          'Номер',
          'Позиция Смета',
          'Категория',
          'Цена смета',
          'Наименование',
          'Примечание',
          'Кол-во',
          'Ед.из.',
          'Прораб',
          'Дата',
          'Проект',
        ],
      ],
      {
        origin: 'A1',
      }
    )
    writeFileXLSX(workbook, 'Applications.xlsx')
  }

  return (
    <Spin spinning={isLoading}>
      <div className={styles.actionButtonsContainer}>
        <div className={styles.infoContainer}>
          <div>
            <Button type="primary" disabled={selectedKeys.length === 0 || isForeman} onClick={createApplicationHandler}>
              Сформировать заявку
            </Button>
            <Button icon={<DownloadOutlined />} style={{ marginLeft: 15 }} type="dashed" onClick={exportToXLS}>
              Выгрузить в XLS
            </Button>
          </div>
          <div>
            <span style={{ marginRight: 15 }}>Для прокрутки списка вправо зажмите SHIFT и используйте колесико</span>
            <Button icon={<SettingOutlined />} onClick={() => setTableSettingsVisible(true)} />
          </div>
        </div>
      </div>
      <Table
        rowKey="id"
        rowSelection={rowSelection}
        size="small"
        bordered
        pagination={tablePagination}
        columns={filteredColumns}
        dataSource={applicationItems}
        scroll={{
          x: 2500,
        }}
      />
      <ApplicationSelectModal
        showModal={showAddMobile}
        onCancel={(update) => {
          setShowAddMobile(false)
          if (update) {
            getApplications()
          }
        }}
        applicationItem={selectedItem}
      />
      <ColumnsSettings
        data={storedData}
        showModal={isTableSettingsVisible}
        onChancel={() => setTableSettingsVisible(false)}
        onSave={(data) => setStoredData(data)}
      />
    </Spin>
  )
}

export default ApplicationItemsTable
